import { useState } from 'react';
import GastoFormModal from './GastoFormModal';
import ConfirmModal from './ConfirmModal';

export default function GastosView({
  gastos,
  hortelanos,
  currentHortelano,
  onSaveGasto,
  onToggleGastoPaid,
  onSettleMyPart,
  onDeleteGasto
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [selectedGasto, setSelectedGasto] = useState(null);
  
  // Confirm delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [gastoToDelete, setGastoToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('historial'); // 'historial' | 'morosos'

  const N = hortelanos.length || 1;

  // Calculos generales
  const totalHuerto = gastos.reduce((sum, g) => sum + Number(g.cantidad), 0);

  // Calcular balance del hortelano actual
  let balanceActivo = 0;
  gastos.forEach(g => {
    if (g.liquidado) return;
    const share = Number(g.cantidad) / N;
    if (g.pagado_por === currentHortelano.id) {
      // Yo pagué, otros me deben. Me deben share por cada moroso restante.
      balanceActivo += share * (g.morosos?.length || 0);
    } else {
      // Pagó otro. Si yo estoy en el array de morosos, debo mi parte.
      if (g.morosos?.includes(currentHortelano.id)) {
        balanceActivo -= share;
      }
    }
  });

  // Filtrar gastos pendientes y pagados para el usuario actual
  const gastosPendientes = gastos.filter(g => 
    !g.liquidado && (
      g.morosos?.includes(currentHortelano.id) || 
      g.pagado_por === currentHortelano.id
    )
  );

  const gastosPagados = gastos.filter(g => 
    g.liquidado || (
      !g.liquidado && 
      !g.morosos?.includes(currentHortelano.id) && 
      g.pagado_por !== currentHortelano.id
    )
  );

  // Calcular deudas individuales agregadas para la lista de morosos
  const deudasMap = {};
  hortelanos.forEach(h => {
    deudasMap[h.id] = {};
    hortelanos.forEach(other => {
      if (h.id !== other.id) {
        deudasMap[h.id][other.id] = 0;
      }
    });
  });

  gastos.forEach(g => {
    if (g.liquidado) return;
    const share = Number(g.cantidad) / N;
    const acreedorId = g.pagado_por;
    if (!g.morosos || g.morosos.length === 0) return;

    g.morosos.forEach(deudorId => {
      if (deudorId !== acreedorId && deudasMap[deudorId] && deudasMap[deudorId][acreedorId] !== undefined) {
        deudasMap[deudorId][acreedorId] += share;
      }
    });
  });

  const listaDeudas = [];
  Object.keys(deudasMap).forEach(deudorId => {
    Object.keys(deudasMap[deudorId]).forEach(acreedorId => {
      const cantidad = deudasMap[deudorId][acreedorId];
      if (cantidad > 0.009) {
        const deudor = hortelanos.find(h => h.id === deudorId);
        const acreedor = hortelanos.find(h => h.id === acreedorId);
        if (deudor && acreedor) {
          listaDeudas.push({
            deudor,
            acreedor,
            cantidad
          });
        }
      }
    });
  });

  const handleEdit = (gasto) => {
    setSelectedGasto(gasto);
    setFormOpen(true);
  };

  const handleAddNew = () => {
    setSelectedGasto(null);
    setFormOpen(true);
  };

  const handleSave = (gastoData) => {
    onSaveGasto(gastoData);
    setFormOpen(false);
    setSelectedGasto(null);
  };

  const handleDeleteRequest = (gasto) => {
    setGastoToDelete(gasto);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (gastoToDelete) {
      onDeleteGasto(gastoToDelete.id);
    }
    setDeleteConfirmOpen(false);
    setGastoToDelete(null);
  };

  const formatFecha = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  const renderGastoCard = (g) => {
    const pagador = hortelanos.find(h => h.id === g.pagado_por);
    const pagadorName = pagador ? pagador.nombre : 'Desconocido';
    const pagadorSexo = pagador ? pagador.sexo : 'M';
    
    const share = Number(g.cantidad) / N;
    const isPayer = g.pagado_por === currentHortelano.id;
    const isDebtor = g.morosos?.includes(currentHortelano.id);
    
    const deudores = hortelanos.filter(h => g.morosos?.includes(h.id));
    const deudoresNames = deudores.map(h => h.nombre).join(', ');

    return (
      <div 
        key={g.id}
        style={{
          backgroundColor: 'var(--surface-color)',
          borderRadius: 'var(--radius-md)',
          padding: '16px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          position: 'relative',
          transition: 'var(--transition)',
          opacity: g.liquidado ? 0.75 : 1
        }}
      >
        {/* Top: Concepto, Cantidad y Fecha */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, paddingRight: '8px' }}>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              {g.concepto}
            </h4>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px' }}>
              📅 {formatFecha(g.fecha)}
            </span>
          </div>
          <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
              {Number(g.cantidad).toFixed(2)} €
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              ({share.toFixed(2)} € c/u)
            </span>
          </div>
        </div>

        {/* Mid: Quien pagó y lista de morosos */}
        <div style={{
          fontSize: '13px',
          padding: '10px 12px',
          borderRadius: 'var(--radius-sm)',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.02)',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}>
          <div>
            <span style={{ color: 'var(--text-secondary)' }}>Pagado por:</span>{' '}
            <strong style={{ color: 'var(--text-primary)' }}>
              {pagadorSexo === 'M' ? '👨‍🌾' : '👩‍🌾'} {pagadorName} {isPayer && '(Tú)'}
            </strong>
          </div>

          {g.liquidado ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#4CAF50', fontWeight: '600' }}>
              <span>✅ Liquidado para todos</span>
            </div>
          ) : (
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>Morosos:</span>{' '}
              {deudores.length > 0 ? (
                <span style={{ color: 'var(--danger-color)', fontWeight: '600' }}>
                  {deudoresNames}
                </span>
              ) : (
                <span style={{ color: '#4CAF50', fontWeight: '600' }}>Ninguno</span>
              )}
            </div>
          )}
        </div>

        {/* Bottom: Alertas de Balance Individual y Acciones */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
          <div>
            {!g.liquidado && (
              <>
                {isDebtor && (
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'var(--danger-color)',
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius-sm)'
                  }}>
                    💸 Debes {share.toFixed(2)} €
                  </span>
                )}
                {isPayer && deudores.length > 0 && (
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#4CAF50',
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius-sm)'
                  }}>
                    💰 Te deben {(share * deudores.length).toFixed(2)} €
                  </span>
                )}
                {!isPayer && !isDebtor && (
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'var(--text-secondary)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius-sm)'
                  }}>
                    👍 Pagado
                  </span>
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Acciones para marcar como pagado / pagar parte */}
            {!g.liquidado && isDebtor && (
              <button
                onClick={() => onSettleMyPart(g.id)}
                className="btn"
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  borderRadius: 'var(--radius-sm)',
                  width: 'auto',
                  height: '32px',
                  backgroundColor: 'var(--accent-color)',
                  color: '#121212'
                }}
              >
                ✔️ Pagar mi parte
              </button>
            )}

            {/* Botones de edición/borrado */}
            <button
              onClick={() => handleEdit(g)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Editar gasto"
            >
              ✏️
            </button>
            <button
              onClick={() => handleDeleteRequest(g)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--danger-color)',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Eliminar gasto"
            >
              🗑️
            </button>
          </div>
        </div>

      </div>
    );
  };

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingBottom: '20px' }}>
      
      {/* Resumen Financiero Glassmorphic */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        
        {/* Card 1: Gasto Total */}
        <div style={{
          flex: '1 1 200px',
          backgroundColor: 'var(--surface-color)',
          padding: '20px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '110px'
        }}>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            💰 Gasto Total del Huerto
          </span>
          <span style={{ fontSize: '28px', fontWeight: '700', color: 'var(--primary-color)', marginTop: '8px' }}>
            {totalHuerto.toFixed(2)} €
          </span>
        </div>

        {/* Card 2: Balance Personal */}
        <div style={{
          flex: '1 1 200px',
          backgroundColor: 'var(--surface-color)',
          padding: '20px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '110px'
        }}>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            ⚖️ Mi Balance Pendiente
          </span>
          <span style={{
            fontSize: '28px',
            fontWeight: '700',
            color: balanceActivo > 0 ? '#4CAF50' : balanceActivo < 0 ? 'var(--danger-color)' : 'var(--text-secondary)',
            marginTop: '8px'
          }}>
            {balanceActivo > 0 ? `+${balanceActivo.toFixed(2)}` : balanceActivo.toFixed(2)} €
          </span>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            {balanceActivo > 0 ? 'Te deben otros hortelanos' : balanceActivo < 0 ? 'Debes a otros hortelanos' : 'Estás al día'}
          </span>
        </div>
      </div>

      {/* Selector de Vista (Pestañas) */}
      <div style={{
        display: 'flex',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        padding: '4px',
        borderRadius: 'var(--radius-md)',
        gap: '4px'
      }}>
        <button
          onClick={() => setActiveTab('historial')}
          style={{
            flex: 1,
            padding: '10px 16px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            backgroundColor: activeTab === 'historial' ? 'var(--primary-color)' : 'transparent',
            color: activeTab === 'historial' ? '#121212' : 'var(--text-secondary)',
            transition: 'var(--transition)'
          }}
        >
          📋 Historial de Gastos
        </button>
        <button
          onClick={() => setActiveTab('morosos')}
          style={{
            flex: 1,
            padding: '10px 16px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            backgroundColor: activeTab === 'morosos' ? 'var(--accent-color)' : 'transparent',
            color: activeTab === 'morosos' ? '#121212' : 'var(--text-secondary)',
            transition: 'var(--transition)'
          }}
        >
          👥 Lista de Morosos
        </button>
      </div>

      {activeTab === 'historial' ? (
        <>
          {/* Button: Nuevo Gasto */}
          <button className="btn" onClick={handleAddNew} style={{ alignSelf: 'stretch', padding: '16px', fontSize: '16px' }}>
            ➕ Añadir Gasto
          </button>

          {/* List of expenses */}
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: 'var(--text-primary)' }}>
              Historial de Gastos
            </h2>

            {gastos.length === 0 ? (
              <div style={{
                backgroundColor: 'var(--surface-color)',
                padding: '32px',
                borderRadius: 'var(--radius-md)',
                textAlign: 'center',
                color: 'var(--text-secondary)',
                border: '1px dashed rgba(255, 255, 255, 0.1)'
              }}>
                📭 No hay gastos registrados en este huerto todavía.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                
                {/* Gastos Pendientes */}
                <div>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'var(--accent-color)',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>🔴 Gastos Pendientes</span>
                    <span style={{
                      backgroundColor: 'rgba(255, 193, 7, 0.15)',
                      color: 'var(--accent-color)',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      {gastosPendientes.length}
                    </span>
                  </h3>

                  {gastosPendientes.length === 0 ? (
                    <div style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      padding: '20px',
                      borderRadius: 'var(--radius-md)',
                      textAlign: 'center',
                      color: 'var(--text-secondary)',
                      fontSize: '14px',
                      border: '1px dashed rgba(255, 255, 255, 0.05)'
                    }}>
                      🎉 ¡Estás al día! No tienes gastos pendientes.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {gastosPendientes.map(renderGastoCard)}
                    </div>
                  )}
                </div>

                {/* Gastos Pagados */}
                <div>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#4CAF50',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '8px'
                  }}>
                    <span>✅ Gastos Pagados o Liquidados</span>
                    <span style={{
                      backgroundColor: 'rgba(76, 175, 80, 0.15)',
                      color: '#4CAF50',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      {gastosPagados.length}
                    </span>
                  </h3>

                  {gastosPagados.length === 0 ? (
                    <div style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      padding: '20px',
                      borderRadius: 'var(--radius-md)',
                      textAlign: 'center',
                      color: 'var(--text-secondary)',
                      fontSize: '14px',
                      border: '1px dashed rgba(255, 255, 255, 0.05)'
                    }}>
                      📭 No hay gastos pagados todavía.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {gastosPagados.map(renderGastoCard)}
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        </>
      ) : (
        /* Lista de Morosos View */
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: 'var(--text-primary)' }}>
            Deudas Acumuladas
          </h2>

          {listaDeudas.length === 0 ? (
            <div style={{
              backgroundColor: 'var(--surface-color)',
              padding: '32px',
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
              color: 'var(--text-secondary)',
              border: '1px dashed rgba(255, 255, 255, 0.1)'
            }}>
              🎉 Todos están al día. ¡No hay deudas pendientes en el huerto!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {listaDeudas.map((deuda, idx) => {
                const isDeudorYo = deuda.deudor.id === currentHortelano.id;
                const isAcreedorYo = deuda.acreedor.id === currentHortelano.id;
                
                return (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: 'var(--surface-color)',
                      borderRadius: 'var(--radius-md)',
                      padding: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      position: 'relative',
                      borderLeft: isDeudorYo
                        ? '4px solid var(--danger-color)'
                        : isAcreedorYo
                        ? '4px solid #4CAF50'
                        : '4px solid rgba(255,255,255,0.1)',
                      transition: 'var(--transition)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <div style={{ fontSize: '24px' }}>
                        {isDeudorYo ? '💸' : isAcreedorYo ? '💰' : '🤝'}
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                          <span style={{ fontWeight: '600' }}>
                            {deuda.deudor.sexo === 'M' ? '👨‍🌾' : '👩‍🌾'} {deuda.deudor.nombre} {isDeudorYo && '(Tú)'}
                          </span>
                          <span style={{ color: 'var(--text-secondary)', margin: '0 8px' }}>debe a</span>
                          <span style={{ fontWeight: '600' }}>
                            {deuda.acreedor.sexo === 'M' ? '👨‍🌾' : '👩‍🌾'} {deuda.acreedor.nombre} {isAcreedorYo && '(Tú)'}
                          </span>
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          Saldo acumulado de gastos pendientes
                        </span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: isDeudorYo ? 'var(--danger-color)' : isAcreedorYo ? '#4CAF50' : 'var(--text-primary)'
                      }}>
                        {deuda.cantidad.toFixed(2)} €
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal del Formulario de Gasto */}
      <GastoFormModal 
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedGasto(null);
        }}
        onSave={handleSave}
        initialData={selectedGasto}
      />

      {/* Modal de Confirmación de Borrado */}
      <ConfirmModal 
        isOpen={deleteConfirmOpen}
        title="¿Eliminar Gasto?"
        message={`¿Estás seguro de que deseas eliminar el gasto "${gastoToDelete?.concepto}" por valor de ${Number(gastoToDelete?.cantidad).toFixed(2)} €? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setGastoToDelete(null);
        }}
      />

    </div>
  );
}
